import type {
  Control,
  ControlStatus,
  GapAnswer,
  PhaseStatus,
  TrustFamily,
} from '@wedpo/soc2';
import { MOCK_PHASES, type MockPhaseId, type ViewId } from '@/lib/store';
import {
  Gauges,
  KpiTiles,
  NextActions,
  Stepper,
  type StepStatus,
  type TileSpec,
} from '@/components/dashboard';

type Assessed = Control & { status: ControlStatus };

/** Traffic-light thresholds shared across the hub: >=75 green, >=45 amber. */
const ragColor = (p: number) =>
  p >= 75 ? 'var(--rag-green)' : p >= 45 ? 'var(--rag-amber)' : 'var(--rag-red)';
const ragClass = (p: number): 'ok' | 'warn' | 'bad' =>
  p >= 75 ? 'ok' : p >= 45 ? 'warn' : 'bad';

const FAM_ORDER: TrustFamily[] = ['CC', 'A', 'C', 'PI', 'P'];

const OPTIONAL: { fam: TrustFamily; nm: string; ds: string }[] = [
  { fam: 'A', nm: 'Availability', ds: 'Uptime, capacity, backup & recovery · +5 controls' },
  { fam: 'C', nm: 'Confidentiality', ds: 'Protecting confidential data · +2 controls' },
  { fam: 'P', nm: 'Privacy', ds: 'PII lifecycle & data subject rights · +10 controls' },
  {
    fam: 'PI',
    nm: 'Processing Integrity',
    ds: 'Complete, accurate processing · +4 controls',
  },
];

const statusWord = (s: PhaseStatus) =>
  s === 'done' ? 'Complete' : s === 'active' ? 'In progress' : 'Not started';

export function Overview({
  company,
  readiness,
  mockStatus,
  assessed,
  answers,
  evidence,
  scope,
  toggleFam,
  go,
}: {
  company: string;
  readiness: number;
  mockStatus: Record<MockPhaseId, PhaseStatus>;
  assessed: Assessed[];
  answers: Record<string, GapAnswer>;
  evidence: Record<string, string[]>;
  scope: Set<TrustFamily>;
  toggleFam: (f: TrustFamily) => void;
  go: (v: ViewId) => void;
}) {
  const inScope = assessed.filter((c) =>
    c.trustFamilies.some((f) => scope.has(f)),
  );
  const total = inScope.length;
  const ready = inScope.filter((c) => c.status === 'met').length;
  const gaps = inScope.filter((c) => c.status === 'gap').length;
  const rated = inScope.filter((c) => answers[c.code]).length;
  const withEvidence = inScope.filter(
    (c) => (evidence[c.code] ?? []).length > 0,
  ).length;
  const phasesDone = MOCK_PHASES.filter((p) => mockStatus[p.id] === 'done')
    .length;
  const assessProg = total ? Math.round((rated / total) * 100) : 0;
  const evProg = total ? Math.round((withEvidence / total) * 100) : 0;

  const cur = MOCK_PHASES.find((p) => mockStatus[p.id] === 'active') ??
    MOCK_PHASES[0];

  // Stepper: real per-phase progress where we have it, else a status word.
  const progressFor = (id: MockPhaseId, s: PhaseStatus): string => {
    if (id === 'assess') return s === 'locked' ? 'Not started' : `${assessProg}%`;
    if (id === 'evidence') return s === 'locked' ? 'Not started' : `${evProg}%`;
    return statusWord(s);
  };
  const stepNodes = MOCK_PHASES.map((p) => ({
    id: p.id,
    num: String(Number(p.num)),
    name: p.name,
    tag: p.tag,
    status: mockStatus[p.id] as StepStatus,
    prog: progressFor(p.id, mockStatus[p.id]),
  }));

  // Gauges: ALL + one per in-scope family, on real per-family readiness.
  const famStats = (f: TrustFamily) => {
    const fam = assessed.filter((c) => c.trustFamilies.includes(f));
    if (!fam.length) return { pct: 0, ready: 0, total: 0 };
    const score = fam.reduce(
      (a, c) => a + (c.status === 'met' ? 1 : c.status === 'partial' ? 0.5 : 0),
      0,
    );
    return {
      pct: Math.round((score / fam.length) * 100),
      ready: fam.filter((c) => c.status === 'met').length,
      total: fam.length,
    };
  };
  const gaugeItems = [
    { id: 'ALL', pct: readiness, sub: `${ready}/${total}`, color: ragColor(readiness) },
    ...FAM_ORDER.filter((f) => scope.has(f)).map((f) => {
      const s = famStats(f);
      return { id: f, pct: s.pct, sub: `${s.ready}/${s.total}`, color: ragColor(s.pct) };
    }),
  ];

  // Next actions: derived from real state, capped at 5.
  const actions: { t: string; m: string; phase: string }[] = [];
  const firstUnrated = inScope.find((c) => !answers[c.code]);
  if (firstUnrated)
    actions.push({
      t: `Rate control ${firstUnrated.code}`,
      m: firstUnrated.name,
      phase: 'Assess',
    });
  const firstGap = inScope.find((c) => c.status === 'gap');
  if (firstGap)
    actions.push({
      t: `Close gap: ${firstGap.name}`,
      m: `${firstGap.code} · high priority`,
      phase: 'Plan',
    });
  const needEvidence = inScope.find(
    (c) => c.status === 'met' && !(evidence[c.code] ?? []).length,
  );
  if (needEvidence)
    actions.push({
      t: `Attach evidence for ${needEvidence.code}`,
      m: needEvidence.name,
      phase: 'Evidence',
    });
  actions.push({
    t: 'Draft the System Description',
    m: 'AICPA DC 1-9 narrative auditors read first',
    phase: 'Finalize',
  });
  if (actions.length === 1 && !firstUnrated && !firstGap && !needEvidence)
    actions.unshift({
      t: "You're on track",
      m: 'No open assessment or evidence actions right now',
      phase: 'Assess',
    });

  const tiles: TileSpec[] = [
    {
      k: 'Overall readiness',
      v: `${readiness}%`,
      delta: 'across in-scope controls',
      deltaClass: 'flat',
      rag: ragClass(readiness),
    },
    {
      k: 'Controls ready',
      v: ready,
      delta: `${Math.max(0, total - ready)} remaining`,
      deltaClass: 'flat',
      rag: 'ok',
    },
    {
      k: 'Open gaps',
      v: gaps,
      delta: 'in the remediation roadmap',
      deltaClass: 'warn',
      rag: 'bad',
    },
    {
      k: 'Phases complete',
      v: (
        <>
          {phasesDone}
          <small>/6</small>
        </>
      ),
      delta: `current: ${cur.name}`,
      deltaClass: 'flat',
      rag: phasesDone >= 3 ? 'ok' : 'warn',
    },
  ];

  return (
    <section>
      <div className="pagehead">
        <div>
          <div className="crumb">{company} · SOC 2 Type 2</div>
          <h1>Overview</h1>
          <p className="lead">
            Where you are, what's next, and the whole path at a glance.
          </p>
        </div>
        <div className="headbtns">
          <button className="btn btn-ghost" onClick={() => go(cur.primary)}>
            Continue: {cur.name}
          </button>
          <button className="btn btn-grad" onClick={() => go('p2')}>
            Start gap analysis →
          </button>
        </div>
      </div>

      <KpiTiles tiles={tiles} />

      <div className="panel accent mb18">
        <div className="ph">
          <h3>Your SOC 2 journey</h3>
          <span className="hint">
            {phasesDone} of 6 phases complete · click a phase to open
          </span>
        </div>
        <Stepper
          nodes={stepNodes}
          onOpen={(id) => {
            const p = MOCK_PHASES.find((m) => m.id === id);
            if (p) go(p.primary);
          }}
        />
      </div>

      <div className="dgrid2">
        <div className="panel">
          <div className="ph">
            <h3>Next actions</h3>
            <span className="hint">across all phases</span>
          </div>
          <NextActions items={actions.slice(0, 5)} />
        </div>
        <div className="panel">
          <div className="ph">
            <h3>Readiness by criteria</h3>
            <span className="hint">traffic-light · ready / mapped</span>
          </div>
          <Gauges items={gaugeItems} />
        </div>
      </div>

      <div className="panel mb18">
        <div className="ph">
          <h3>Scope</h3>
          <span className="hint">trust services criteria · tap to toggle</span>
        </div>
        <div className="pbody">
          <div className="grid2">
            <div className="scope fixed">
              <div>
                <div className="nm">Security (Common Criteria)</div>
                <div className="ds">Mandatory baseline · CC1-CC9 · 66 controls</div>
              </div>
              <span className="sw" />
            </div>
            {OPTIONAL.map((o) => (
              <div
                key={o.fam}
                className={`scope${scope.has(o.fam) ? ' on' : ''}`}
                onClick={() => toggleFam(o.fam)}
              >
                <div>
                  <div className="nm">{o.nm}</div>
                  <div className="ds">{o.ds}</div>
                </div>
                <span className="sw" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
